import { Component } from 'react';
import { View, ScrollView } from 'react-native';
import { Button, Popup, DocumentTitle, Loading, LabelledView, PickerInput, Text, CheckboxInput } from '@symbolic/rn-lib';
import NumberInput from '~/components/number-input';
import { connect } from '@symbolic/redux';
import { resourceActions } from '~/redux';
import { api } from '@symbolic/lib';

import AdminMenu from '~/components/admin-menu';
import SecondaryHeader from '~/components/secondary-header';

import K from '~/k';
import settingsIcon from '~/assets/settings-icon.png';
import _, { filter } from 'lodash';

class AdminMechanismSpacingRulePage extends Component {
  state = {
    isSettingsPopupVisible: false,
    isLoading: true,
  };

  async componentDidMount() {
    var {mechanismSpacingRuleId, session: {activeOrg: {id: orgId}}} = this.props;

    var mechanismSpacingRules = await api.get('mechanismSpacingRules', {where: {id: mechanismSpacingRuleId, orgId}});
    var products = await api.get('products', {where: {orgId}});
    var productOptions = await api.get('productOptions', {where: {orgId}});
    var productCategories = await api.get('productCategories', {where: {orgId}});

    await this.props.trackMechanismSpacingRules({mechanismSpacingRules});
    await this.props.trackProducts({products});
    await this.props.trackProductOptions({productOptions});
    await this.props.trackProductCategories({productCategories});

    setTimeout(() => {
      this.setState({isLoading: false});
    });
  }

  toggleSettingsPopup = () => this.setState({isSettingsPopupVisible: !this.state.isSettingsPopupVisible});

  deleteMechanismSpacingRule = async () => {
    if (await confirm('Are you sure you want to delete this mechanism spacing rule?')) {
      this.props.history.push('/admin/mechanism-spacing-rules');

      setTimeout(() => {
        this.props.destroyMechanismSpacingRule({id: this.props.mechanismSpacingRule.id});
      });
    }
  };

  duplicateMechanismSpacingRule = async () => {
    var {mechanismSpacingRule} = this.props;

    var newRule = await api.create('mechanismSpacingRule', {
      ..._.omit(mechanismSpacingRule, ['id', 'created', 'lastUpdated', 'lastUpdatedBy', 'deleted']),
      title: `${mechanismSpacingRule.title} (copy)`
    });

    this.props.trackMechanismSpacingRules({mechanismSpacingRules: [newRule]});

    setTimeout(() => {
      if (confirm('This mechanism spacing rule has been successfully duplicated. Would you like to be redirected to the new rule?')) {
        this.props.history.push(`/admin/mechanism-spacing-rules/${newRule.id}`);
      }
    });
  };

  handleFieldChange = (field, value) => {
    this.props.updateMechanismSpacingRule({id: this.props.mechanismSpacingRule.id, props: {[field]: value}});
  };

  handleProductCategoryChange = (value) => {
    // When product category changes, clear the selected products
    this.props.updateMechanismSpacingRule({
      id: this.props.mechanismSpacingRule.id,
      props: {
        productCategoryId: value === 0 ? null : value,
        productIds: []
      }
    });
  };

  handleProductToggle = (productId) => {
    const currentProductIds = this.props.mechanismSpacingRule.productIds || [];
    const newProductIds = currentProductIds.includes(productId)
      ? currentProductIds.filter(id => id !== productId)
      : [...currentProductIds, productId];

    this.handleFieldChange('productIds', newProductIds);
  };

  render() {
    if (this.state.isLoading) {
      return (
        <View style={{flex: 1, flexDirection: 'row'}}>
          <AdminMenu activeOrg={this.props.session.activeOrg} />
          <View style={{flex: 1}}>
            <Loading />
          </View>
        </View>
      );
    }

    const {mechanismSpacingRule, productsById, productOptionsById, productCategoriesById} = this.props;

    if (!mechanismSpacingRule) {
      return (
        <View style={{flex: 1, flexDirection: 'row'}}>
          <AdminMenu activeOrg={this.props.session.activeOrg} />
          <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
            <Text>Mechanism Spacing Rule not found</Text>
          </View>
        </View>
      );
    }

    var styles = {
      inputGroup: {flexDirection: 'row', gap: K.margin, marginBottom: K.margin, flexWrap: 'wrap'},
      input: {height: K.inputHeight, width: 300},
      helpText: {fontStyle: 'italic', opacity: 0.5, paddingHorizontal: K.spacing, marginBottom: K.margin, width: 900}
    };

    var filteredProducts = [];

    if (mechanismSpacingRule.productCategoryId) {
      filteredProducts = _.filter(productsById, product => product.productCategoryId === mechanismSpacingRule.productCategoryId);
    }

    return (
      <DocumentTitle title={`Admin: Mechanism Spacing Rule ${mechanismSpacingRule.id}`}>
        <View style={{flex: 1, flexDirection: 'row'}}>
          <AdminMenu activeOrg={this.props.session.activeOrg} />
          <View style={{flex: 1}}>
            <SecondaryHeader
              title={mechanismSpacingRule.title}
              titleIsEditable
              onTitleChange={({value}) => this.handleFieldChange('title', value)}
              subTitle={`ID: ${mechanismSpacingRule.id}`}
              rightComponent={(
                <View style={{alignItems: 'flex-end'}}>
                  <Button dark label='Duplicate' onPress={this.duplicateMechanismSpacingRule} style={{marginRight: K.spacing}}/>
                </View>
              )}
              belowHeaderComponent={
                <View style={{marginTop: K.spacing * 2, flexDirection: 'row', alignItems: 'center', gap: K.spacing}}>
                  <Button
                    onPress={this.toggleSettingsPopup}
                    icon={settingsIcon}
                    style={{backgroundColor: 'transparent', ...K.defaultIconSize}}
                  />
                </View>
              }
            />
            <ScrollView style={{flex: 1}} contentContainerStyle={{padding: K.spacing * 2}}>
              <View>
                <Text style={{...styles.helpText}}>
                  {'*Mechanism spacing rules have not yet been adapted for all product categories. If a product category or product is not selectable below, its spacing rules are hardcoded in the codebase. Please contact a developer to modify the spacing rules or build out admin tool support.'}
                </Text>
                <View style={{...styles.inputGroup, marginTop: K.spacing, flexDirection: 'column'}}>
                  <LabelledView label="Product Category">
                    <PickerInput
                      style={{...styles.input}}
                      showDownArrow={true}
                      options={[
                        ..._.map(_.filter(productCategoriesById, productCategory => _.includes([1, 3], productCategory.id)), productCategory => {
                          return {value: productCategory.id, title: `${productCategory.title} (ID: ${productCategory.id})`};
                        })
                      ]}
                      value={mechanismSpacingRule.productCategoryId}
                      onChange={({value}) => this.handleProductCategoryChange(parseInt(value))}
                    />
                  </LabelledView>
                  <LabelledView label="Products" styles={{outerView: {marginTop: K.spacing}}}>
                    {_.map(filteredProducts, product => {
                      return (
                        <CheckboxInput
                          key={`product-${product.id}`}
                          label={`${product.title} (ID: ${product.id})`}
                          value={_.includes(mechanismSpacingRule.productIds, product.id)}
                          onChange={() => this.handleProductToggle(product.id)}
                          style={{marginBottom: K.margin, ...styles.input}}
                        />
                      );
                    })}
                  </LabelledView>
                </View>
              </View>
              <View>
                {mechanismSpacingRule.productCategoryId === 1 && (
                  <Text style={{...styles.helpText}}>
                    {'*One of the following back box fields must be specified. "Back Box Type" applies the rule to a category of backboxes. "Specific Backbox" only applies the rule when a specific backbox is selected and will override the "Back Box Type" setting.'}
                  </Text>
                )}
                <View style={{...styles.inputGroup}}>
                  <LabelledView gray label="Back Box Type">
                    <PickerInput
                      style={{...styles.input}}
                      showDownArrow={true}
                      options={[
                        {value: null, title: '-'},
                        {value: 'MELUS', title: 'MELUS'},
                        {value: 'MUD RING', title: 'Mud Ring'}
                      ]}
                      value={mechanismSpacingRule.backBoxType}
                      onChange={({value}) => this.handleFieldChange('backBoxType', value)}
                    />
                  </LabelledView>
                  {mechanismSpacingRule.productCategoryId === 1 && (
                    <LabelledView gray label="Specific Back Box">
                      <PickerInput
                        style={{...styles.input}}
                        showDownArrow={true}
                        options={[
                          {value: 0, title: '-'},
                          ..._.map(_.filter(productOptionsById, {productPropertyId: 29}), productOption => {
                            return {value: productOption.id, title: `${productOption.title} (ID: ${productOption.id})`};
                          })
                        ]}
                        value={mechanismSpacingRule.backBoxId || 0}
                        onChange={({value}) => this.handleFieldChange('backBoxId', value)}
                      />
                    </LabelledView>
                  )}
                </View>
              </View>
              <View>
                <Text style={{...styles.helpText}}>
                  {'*The following fields define mechanism spacing, but there are some hardcoded exceptions for specific products/mechanism layouts.'}
                </Text>
                <View style={{...styles.inputGroup}}>
                  <LabelledView gray label="Axis">
                    <PickerInput
                      style={{...styles.input}}
                      buttonStyle={{backgroundColor: K.colors.gray}}
                      showDownArrow={true}
                      options={[
                        {value: 'x', title: 'X (Horizontal)'},
                        {value: 'y', title: 'Y (Vertical)'}
                      ]}
                      value={mechanismSpacingRule.axis}
                      onChange={({value}) => this.handleFieldChange('axis', value)}
                    />
                  </LabelledView>
                  {mechanismSpacingRule.axis === 'x' && (
                    <>
                      <LabelledView gray label="Number of Columns">
                        <NumberInput
                          style={{...styles.input}}
                          placeholder="Unspecified"
                          value={_.get(mechanismSpacingRule, 'columns', 0)}
                          onChange={({value}) => this.handleFieldChange('columns', value)}
                        />
                      </LabelledView>
                      <LabelledView gray label="Width (mm)">
                        <NumberInput
                          style={{...styles.input}}
                          placeholder="Unspecified"
                          value={_.get(mechanismSpacingRule, 'width', 0)}
                          onChange={({value}) => this.handleFieldChange('width', value)}
                        />
                      </LabelledView>
                    </>
                  )}
                  {mechanismSpacingRule.axis === 'y' && (
                    <>
                      <LabelledView gray label="Number of Rows">
                        <NumberInput
                          style={{...styles.input}}
                          placeholder="Unspecified"
                          value={_.get(mechanismSpacingRule, 'rows', 0)}
                          onChange={({value}) => this.handleFieldChange('rows', value)}
                        />
                      </LabelledView>
                      <LabelledView gray label="Height (mm)">
                        <NumberInput
                          style={{...styles.input}}
                          placeholder="Unspecified"
                          value={_.get(mechanismSpacingRule, 'height', 0)}
                          onChange={({value}) => this.handleFieldChange('height', value)}
                        />
                      </LabelledView>
                    </>
                  )}
                  <LabelledView gray label="Spacing (mm)">
                    <NumberInput
                      style={{...styles.input}}
                      placeholder="Space between mechanisms"
                      value={_.get(mechanismSpacingRule, 'spacing', 0)}
                      onChange={({value}) => this.handleFieldChange('spacing', value)}
                    />
                  </LabelledView>
                </View>
              </View>
              <View>
                {mechanismSpacingRule.productCategoryId === 1 && (
                  <>
                    <Text style={{...styles.helpText}}>
                      {'*The following fields are optional. Not all products support toggles and buttons. If set, these will override hardcoded defaults for specific products/mechanism layouts.'}
                    </Text>
                    <View style={{...styles.inputGroup}}>
                      <CheckboxInput
                        label="Applies to Toggles"
                        value={mechanismSpacingRule.hasToggle === 1}
                        onChange={() => this.handleFieldChange('hasToggle', mechanismSpacingRule.hasToggle === 1 ? 0 : 1)}
                        style={{...styles.input}}
                      />
                      <CheckboxInput
                        label="Applies to Buttons"
                        value={mechanismSpacingRule.hasButton === 1}
                        onChange={() => this.handleFieldChange('hasButton', mechanismSpacingRule.hasButton === 1 ? 0 : 1)}
                        style={{...styles.input}}
                      />
                    </View>
                  </>
                )}
                {mechanismSpacingRule.productCategoryId === 3 && (
                  <>
                    <Text style={{...styles.helpText}}>
                      {'*Wall outlet spacing rules are structured in a way that requires separate rules for visible and hidden screws.'}
                    </Text>
                    <View style={{...styles.inputGroup}}>
                      <LabelledView gray label="Screw Type">
                        <PickerInput
                          style={{...styles.input}}
                          showDownArrow={true}
                          options={[
                            {value: 0, title: 'Hidden'},
                            {value: 1, title: 'Visible'}
                          ]}
                          value={mechanismSpacingRule.isScrewVisible}
                          onChange={({value}) => this.handleFieldChange('isScrewVisible', parseInt(value))}
                        />
                      </LabelledView>
                    </View>
                  </>
                )}
              </View>
            </ScrollView>

            {this.state.isSettingsPopupVisible && (
              <Popup onClose={this.toggleSettingsPopup}>
                <Button
                  label='Delete'
                  onPress={this.deleteMechanismSpacingRule}
                  style={{backgroundColor: K.colors.deleteRed}}
                />
              </Popup>
            )}
          </View>
        </View>
      </DocumentTitle>
    );
  }
}

export default connect({
  mapState: (state, ownProps) => {
    var mechanismSpacingRuleId = parseInt(ownProps.match.params.mechanismSpacingRuleId);

    return {
      mechanismSpacingRuleId,
      mechanismSpacingRule: _.get(state, `resources.mechanismSpacingRules.byId[${mechanismSpacingRuleId}]`),
      productsById: state.resources.products.byId,
      productOptionsById: state.resources.productOptions.byId,
      productCategoriesById: state.resources.productCategories.byId
    };
  },
  mapDispatch: {
    ..._.pick(resourceActions.mechanismSpacingRules, ['updateMechanismSpacingRule', 'trackMechanismSpacingRules', 'destroyMechanismSpacingRule']),
    ..._.pick(resourceActions.products, ['trackProducts']),
    ..._.pick(resourceActions.productOptions, ['trackProductOptions']),
    ..._.pick(resourceActions.productCategories, ['trackProductCategories'])
  }
})(AdminMechanismSpacingRulePage);
